//
// This file is the shared UserContext and AuthProvider component 
// that is wrapped at very high level in the DOM tree inside index.js.
// 
// It provides the one and only authedUser state and the setter for 
// all the downstream components to use.

// To dump authContext in the component: Add the following lines in the component:
//   console.log("authContext dump:\n" + JSON.stringify(authedUser, null, 2));
//   alert("authContext dump:\n" + JSON.stringify(authedUser, null, 2));

import React, { createContext, useState, useEffect, useContext, useCallback } from "react";
import { loadAuthedUser, saveAuthedUser, cleanAuthedUser } from "./authedUser";
import { setUnauthorizedHandler } from "./api";
import {  useNavigate } from "react-router-dom";

// 1️⃣ Create the (type of) context
export const UserContext = createContext();

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  // 2️⃣ Initialize user state with the authUser object loaded from localStorage 
  // the authedUser state gets the correct initial value when AuthProvider component first renders.
  const [authedUser, setAuthedUser] = useState(loadAuthedUser());  // <- This provides the one and only authedUser state and the setter

  // Optional: sync if storage changes from another tab
  useEffect(() => {
    const handleStorageChange = () => setAuthedUser(loadAuthedUser());
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // A helper function to do all necessary things upon login:
  // useCallback keeps this function's identity stable across re-renders — consumers
  // that depend on it (e.g. effects re-initializing Google Sign-In) would otherwise
  // re-run on every AuthProvider render, not just on actual login/logout.
  const login = useCallback((userData) => {
    userData.avatarImageVersion = Math.random().toString(36).substring(2, 8);
    saveAuthedUser(userData);    // save authedUser in local storage
    setAuthedUser(userData);     // update authedUser in state
  }, []);

  // A helper function to do all necessary things upon logout:
  const logout = useCallback(() => {
    cleanAuthedUser();       // clear authedUser from local storage
    setAuthedUser(null);     // reset authedUser in state
  }, []);

  // Wire up what should happen when the backend rejects a request with 401 (e.g. an
  // expired/revoked token): log the user out and send them to the home page.
  //
  // Note this does NOT register an axios interceptor itself (it used to, and that was the bug —
  // see the long comment above setUnauthorizedHandler in utils/api.js for the full story). The
  // interceptor already exists, permanently, from the moment api.js is first imported. All this
  // effect does is tell that already-existing interceptor what function to call when it sees a
  // 401. That split matters because this effect (being inside AuthProvider, a React component)
  // only runs AFTER React has committed the initial render — which, for a child component that
  // fires an API call in its OWN mount effect, can be too late if the interceptor itself lived
  // here instead of in api.js.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      logout();
      navigate("/");
    });

    // On unmount, stop forwarding 401s to a logout function that (along with its `navigate`)
    // may no longer be valid. In practice AuthProvider wraps the whole app and never unmounts,
    // but this keeps the module-level handler from outliving the component that owns it.
    return () => setUnauthorizedHandler(null);
  }, [logout, navigate]);

  // Now, store these 3 things (authedUser, login, logout) in the provider,
  // so downstream components can retrive them using useContext(UserContext)
  return (
    <UserContext.Provider value={{ authedUser, setAuthedUser, login, logout }}>
      {children}
    </UserContext.Provider>
  );
};

export const useAuth = () => useContext(UserContext);

